import dayGridPlugin from "@fullcalendar/daygrid";
import interactionPlugin from "@fullcalendar/interaction";
import listPlugin from "@fullcalendar/list";
import FullCalendar from "@fullcalendar/react";
import timeGridPlugin from "@fullcalendar/timegrid";
import { Loader2 } from "lucide-react";
import React, { useState, useEffect } from "react";

import { CalendarSkeleton } from "./CalendarSkeleton";

interface CalendarMainProps {
  events: any[];
  isLoading: boolean;
  onEventClick: (clickInfo: any) => void;
  onDateClick: (selectInfo: any) => void;
  calendarRef: React.RefObject<FullCalendar>;
  currentView?: string;
}

export const CalendarMain: React.FC<CalendarMainProps> = ({
  events,
  isLoading,
  onEventClick,
  onDateClick,
  calendarRef,
  currentView = "month",
}) => {
  const [isMobileView, setIsMobileView] = useState(
    typeof window !== "undefined" ? window.innerWidth < 768 : false,
  );

  useEffect(() => {
    const handleResize = () => setIsMobileView(window.innerWidth < 768);
    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, []);

  return (
    <div className="relative flex-1 bg-white rounded-lg shadow-sm border border-gray-200 overflow-hidden">
      {isLoading && (
        <div className="absolute inset-0 z-20 bg-white bg-opacity-90 flex items-center justify-center">
          <div className="flex items-center gap-3">
            <Loader2 className="h-6 w-6 animate-spin text-indigo-600" />
            <span className="text-gray-600 font-medium">Loading events...</span>
          </div>
        </div>
      )}

      {isLoading ? (
        <CalendarSkeleton />
      ) : (
        <FullCalendar
          ref={calendarRef}
          plugins={[
            dayGridPlugin,
            timeGridPlugin,
            interactionPlugin,
            listPlugin,
          ]}
          initialView="dayGridMonth"
          headerToolbar={{
            left: "prev,next today",
            center: "title",
            right: "",
          }}
          events={events}
          eventClick={onEventClick}
          dateClick={onDateClick}
          height={
            isMobileView &&
            (currentView === "month" || currentView === "agenda")
              ? "auto"
              : "100%"
          }
          dayMaxEvents={isMobileView ? 1 : 3}
          moreLinkClick="popover"
          eventDisplay="block"
          displayEventTime={true}
          eventTimeFormat={{
            hour: "numeric",
            minute: "2-digit",
            meridiem: "short",
          }}
          slotMinTime="06:00:00"
          slotMaxTime="22:00:00"
          allDaySlot={true}
          nowIndicator={true}
          selectable={false}
          selectMirror={true}
          dayPopoverFormat={{ month: "long", day: "numeric", year: "numeric" }}
          eventMouseEnter={(info) => {
            info.el.style.cursor = "pointer";
          }}
          eventClassNames={(arg) => {
            const classes = ["fc-event-interactive"];
            if (arg.event.extendedProps.meetingLink) {
              classes.push("category-meeting");
            }
            if (isMobileView) {
              classes.push("fc-event-mobile");
            }
            return classes;
          }}
        />
      )}
    </div>
  );
};
